import React from 'react';
import {useSelector} from 'react-redux';
import {TouchableOpacity, View} from 'react-native';
import FastImage from '../../Utils/FastImage';

import useWindowDimensionsSafe from 'src/Modules/Native/useWindowDimensionsSafe';

/////////////////////// components
import {BodyText} from 'src/Modules/CommonView';

/////////////////////// utils
import {playerPhotoChallengeCompletion} from 'src/Utils/huntFunctions';

import {apiCheckAnyChallenge} from 'src/Utils/apiCalls';
import {navigationRef} from 'src/Nav/navigationHelpers';
import {useUserId} from '../../Hooks/reduxHooks';

const Player = () => {
  const {width} = useWindowDimensionsSafe();

  const userId = useUserId();
  const playerChallengeDataActive =
    useSelector((state) => state.game?.playerChallengeDataActive) || [];
  const groupInfo = useSelector((state) => state.groupInfo);
  const characters = useSelector((state) => state.themeInfo?.characters);

  const handlePlayerChallengeSelection = (challenge) => {
    if (challenge.challenge_medium === 'trivia') {
      return navigationRef.navigate('PlayerTrivia', {
        selectedChallengeId: challenge.completionId,
      });
    }
    if (['photo', 'video', 'Photo']?.includes?.(challenge.challenge_medium)) {
      return navigationRef.navigate('PhotoVideoCamera', {
        onCapture: (data) => {
          const completionObj = {
            ...data,
            ...challenge,
          };
          playerPhotoChallengeCompletion(completionObj, groupInfo);
        },
        onScore: async (data) => {
          const checkParams = {
            challenge_id: challenge.challenge_id,
            photo_url: data?.downloadURL,
            shareUUID: challenge.shareUUID,
            user_id: userId,
            group_id: groupInfo?.groupId,
            share_UUID: data?.shareUUID,
          };
          console.log({checkParams});
          return await apiCheckAnyChallenge(checkParams);
        },
        onFailScore: (data) => {
          // return Alert.alert('You failed the challenge!');
          const {photo_grade = {}, lr_app_challenge_completion_id = 0} = data;
          const {
            feedback_title = 'Close but no Cigar!!!!',
            feedback_body = 'We were not able to detect the required elements in the image!',
          } = photo_grade;

          navigationRef.navigate('ConfirmModal', {
            handleConfirm: async () => {
              navigationRef.goBack();
              handlePlayerChallengeSelection(challenge);
            },
            confirmText: 'Try Again',
            cancelText: 'Report Issue',
            handleCancel: () => {
              navigationRef.goBack();

              navigationRef.navigate('QuestionFeedback', {
                challenge,
                lr_app_challenge_completion_id,
                type: 'daily_challenge',
                userId,
                challenged_by_user: 1,
              });
            },
            thirdOption: 'Cancel',
            handleThird: () => {
              navigationRef.goBack();
            },
            confirmColor: global?.color?.orange,
            cancelColor: global?.color?.blue,
            titleText: feedback_title,
            titleImage: require('src/Images/sadTrophy.png'),
            titleBodyText: feedback_body,
          });
        },
        onReport: () => {
          navigationRef.navigate('QuestionFeedback', {
            challenge: challenge,
            type: 'Player',
          });
        },
        photoUrl: challenge.photoUrl,
        shareUUID: challenge.shareUUID,
        text: challenge.task,
        isVideo: challenge.challenge_medium == 'video',
        feedbackUnits: 'Points',
        feedbackPoints: challenge.points || 10,
      });
    }
  };

  const formatType = (challenge_medium) => {
    const text = {
      photo: 'Photo Challenge',
      video: 'Video Challenge',
      trivia: 'Trivia Challenge',
    };

    return text[(challenge_medium || '').toLowerCase?.()] || 'Challenge';
  };

  const {players = {}} = groupInfo;

  return (
    <View style={{paddingTop: 10}}>
      {!playerChallengeDataActive?.length && (
        <BodyText
          text={`No Player Challenges Yet!`}
          textStyle={{marginBottom: 5, fontWeight: '700'}}
        />
      )}
      {!playerChallengeDataActive?.length && (
        <BodyText
          text={`Check back soon for your player challenges.`}
          textStyle={{marginBottom: 5}}
        />
      )}
      {!!playerChallengeDataActive?.length && (
        <BodyText
          text={`Your Next Challenges:`}
          textStyle={{marginBottom: 5, fontWeight: '700'}}
        />
      )}
      <View
        style={{
          width: width - 10,
          marginHorizontal: 5,
          flexDirection: 'row',
          flexWrap: 'wrap',
        }}>
        {!!playerChallengeDataActive?.length &&
          playerChallengeDataActive.slice(0, 4)?.map?.((challenge, index) => {
            // checking for null objects
            if (!challenge || !challenge.challenge_medium) {
              return <View key={index} />;
            }

            const challengeCharacter = challenge.character;
            const characterData = characters[challengeCharacter] || {};
            const imageSource = characterData?.source;

            return (
              <PlayerChallengeTile
                key={`Player Card ${
                  challenge.challengeId ||
                  challenge.challenge_id ||
                  challenge.id ||
                  123
                } ${challenge.name} ${challenge.completionId}`}
                text={challenge.firstName}
                color={
                  (players[challenge.userId] || {}).photoColor ||
                  global?.color?.orange
                }
                imageSource={{uri: imageSource}}
                points={challenge.pointsPossible}
                handlePress={() => {
                  handlePlayerChallengeSelection(challenge);
                }}
                challengeText={formatType(challenge.challenge_medium)}
              />
            );
          })}
      </View>
    </View>
  );
};

const PlayerChallengeTile = ({
  imageSource,
  text = '',
  points = 50,
  handlePress,
  challengeText = '',
  color = global?.color?.orange,
}) => {
  const {width} = useWindowDimensionsSafe();
  const tileWidth = width / 2 - 15;
  return (
    <TouchableOpacity
      style={{
        ...global?.style.boxShadow,
        backgroundColor: global?.color?.white1,
        width: tileWidth,
        marginHorizontal: 5,
        marginVertical: 5,
        height: 150,
        position: 'relative',
        justifyContent: 'center',
        alignItems: 'center',
        paddingTop: 30,
      }}
      activeOpacity={0.8}
      onPress={handlePress}
      disabled={!handlePress}>
      {!!imageSource && (
        <FastImage
          source={imageSource}
          style={{flex: 1, width: tileWidth - 20, marginVertical: 5}}
          resizeMode={FastImage?.resizeMode?.contain}
        />
      )}
      <View
        style={{
          backgroundColor: color,
          top: 0,
          left: 0,
          width: tileWidth,
          paddingVertical: 5,
          position: 'absolute',
          justifyContent: 'space-between',
          flexDirection: 'row',
          right: 0,
        }}>
        <BodyText
          text={text}
          textStyle={{
            color: global?.color?.white,
            fontSize: 14,
            lineHeight: 14,
            marginLeft: 5,
            marginTop: 5,
            fontWeight: '700',
            marginRight: 0,
          }}
        />
        <BodyText
          text={`${points} pts`}
          textStyle={{
            color: global?.color?.white,
            fontSize: 14,
            lineHeight: 14,
            marginTop: 5,
            marginLeft: 2,
            marginRight: 5,
            fontWeight: '700',
          }}
        />
      </View>
      <BodyText
        text={challengeText}
        textStyle={{
          fontSize: 14,
          fontWeight: '700',
          lineHeight: 16,
          marginVertical: 5,
        }}
      />
    </TouchableOpacity>
  );
};

export default Player;
